'use client';

import { faTriangleExclamation } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { formatText, TextRole } from '@pickleballinc/lib/format-text';
import { getCDNURL } from '@pickleballinc/lib/get-cdn-url';
import { APR_LABELS } from '@pickleballinc/lib/product-labels';
import { Button, InputField } from '@pickleballinc/react-ui';
import { RecaptchaNotice } from '@pickleballinc/ui/components/ui/recaptcha-notice';
import clsx from 'clsx';
import { Fragment, useEffect, useState } from 'react';

import { useNewsletterSubscribeForm } from '@/hooks/newsletter/useSubscribeNewsletterForm';
import { publicEnv } from '@/lib/env';
import { APR_COUNTDOWN_END_DATE, PLACEHOLDER_COUNTDOWN } from '@/modules/apr/countdown/constants';
import { PasswordModal } from '@/modules/apr/countdown/PasswordModal';
import { SVGBGBall } from '@/modules/apr/countdown/svg/SVGBGBall';
import { SVGLeftBGBall } from '@/modules/apr/countdown/svg/SVGLeftBGBall';
import { TimeCard } from '@/modules/apr/countdown/TimeCard';
import { getCountdown } from '@/modules/apr/countdown/utils';
import { CountdownInterface } from '@/types/aprRankings.types';
import { subscribeToAPRCountdown } from '@/utils/actions/apr/subscribeToAPRCountdown';
import { CaptchaAction } from '@/utils/helpers/captcha-actions';
import { FEATURE_FLAGS } from '@/utils/helpers/constants';

export const Countdown = () => {
  const [countdown, setCountdown] = useState<CountdownInterface>(PLACEHOLDER_COUNTDOWN);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const { email, onEmailChange, handleSubscribe, displayError, isPending, isSuccess } =
    useNewsletterSubscribeForm(subscribeToAPRCountdown, CaptchaAction.APR_COUNTDOWN_SUBSCRIBE);

  useEffect(() => {
    if (!FEATURE_FLAGS.ENABLE_APR_COUNTDOWN) return;

    const update = () => {
      setCountdown(getCountdown(APR_COUNTDOWN_END_DATE));
    };

    update();
    const interval = setInterval(update, 1000);

    return () => clearInterval(interval);
  }, []);

  const countdownItems = [
    { value: countdown.days, label: 'Days' },
    { value: countdown.hours, label: 'Hours' },
    { value: countdown.minutes, label: 'Mins' },
    { value: countdown.seconds, label: 'Secs' },
  ];

  return (
    <div className='relative flex min-h-screen items-center justify-center overflow-hidden bg-gray-100 px-0 sm:px-6'>
      <div className='absolute right-0 top-0'>
        <SVGBGBall />
      </div>
      <div className='hidden sm:block absolute bottom-0 left-0'>
        <SVGLeftBGBall />
      </div>
      <div className='relative z-10 flex w-full max-w-3xl flex-col items-center'>
        <img
          src={getCDNURL(
            `${publicEnv.NEXT_PUBLIC_IMAGE_CDN_URL}/global/APR-full-colored.svg`,
            320,
            30
          )}
          alt={APR_LABELS.siteName}
          className='h-[30px] sm:h-[52px] w-auto object-contain mb-12 sm:mb-20'
        />
        <div className='flex flex-col gap-2 items-center text-gray-900 px-8 sm:px-0'>
          <h1 className='text-center text-3xl font-bold md:text-5xl !tracking-[-0.96px] !leading-[38px] sm:!leading-[60px]'>
            {formatText({ text: 'Something exciting is coming', role: TextRole.DescriptiveText })}
          </h1>

          <p className='w-full text-center text-sm'>
            {formatText({
              text: "We are launching something exciting that you wouldn't want to miss.",
              role: TextRole.DescriptiveText,
            })}
          </p>
        </div>

        {FEATURE_FLAGS.ENABLE_APR_COUNTDOWN ? (
          <div className='flex items-center gap-1 sm:gap-3 mt-12 sm:mt-16'>
            {countdownItems.map((item, index) => (
              <Fragment key={item.label}>
                <TimeCard value={item.value} label={item.label} />
                {index !== countdownItems.length - 1 && (
                  <span className='text-sm sm:text-4xl font-bold text-black'>:</span>
                )}
              </Fragment>
            ))}
          </div>
        ) : null}
        <div
          className={clsx(
            'w-full sm:w-[518px] sm:rounded-3xl px-4 py-8 sm:p-8 sm:mt-16 shadow-lg bg-[linear-gradient(259deg,#E6EFE0_13.58%,#9EC284_72.22%)] bg-wpr-700 bg-blend-color-burn',
            FEATURE_FLAGS.ENABLE_APR_COUNTDOWN ? 'mt-12' : 'mt-8'
          )}
        >
          {isSuccess ? (
            <p className='text-center text-base text-white py-12'>
              {formatText({ text: 'Thanks for subscribing!', role: TextRole.DescriptiveText })}
            </p>
          ) : (
            <>
              <div className='flex flex-col sm:flex-row w-full justify-center items-start gap-3'>
                <div className='flex flex-col w-full gap-1.5'>
                  <InputField
                    SuffixIcon={() =>
                      displayError ? (
                        <FontAwesomeIcon icon={faTriangleExclamation} className='text-error-500' />
                      ) : null
                    }
                    className='!rounded-lg px-1.5 !h-9 md:!h-10 shadow-xs focus-within:!shadow-wpr-100 focus-within:!border-wpr-100'
                    inputProps={{
                      className: clsx(displayError && 'text-error-600'),
                    }}
                    value={email}
                    type='email'
                    hasError={!!displayError}
                    placeholder={formatText({
                      text: 'Enter your email',
                      role: TextRole.Placeholder,
                    })}
                    onChange={(e) => {
                      onEmailChange(e.target.value);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleSubscribe();
                      }
                    }}
                    disabled={isPending}
                  />
                  {displayError ? (
                    <span className='text-white text-sm'>
                      {formatText({
                        text: displayError,
                        role: TextRole.ErrorMessage,
                      })}
                    </span>
                  ) : null}
                  <RecaptchaNotice className='text-white text-xs' />
                </div>
                <Button
                  size='md'
                  className='!w-full sm:!w-auto !justify-center !bg-wpr-400 !py-2 !px-3 md:!py-[10px] md:!px-[14px] !text-sm whitespace-nowrap focus:!shadow-wpr-100'
                  variant='primary'
                  onClick={handleSubscribe}
                  disabled={isPending}
                >
                  {formatText({
                    text: isPending ? 'Subscribing...' : 'Notify me',
                    role: TextRole.ButtonLabel,
                  })}
                </Button>
              </div>

              <p className='mt-4 sm:mt-8 text-center text-xs leading-[18px] sm:leading-[24px] sm:text-base text-white'>
                {formatText({
                  text: 'The wait is almost over. We will let you know when our exciting launch goes live!',
                  role: TextRole.DescriptiveText,
                })}
              </p>
            </>
          )}
        </div>
        <div className='pt-4 sm:pt-12'>
          <Button
            size='sm'
            variant='link-gray'
            className='flex items-center justify-center'
            onClick={() => setIsPasswordModalOpen(true)}
          >
            {formatText({
              text: 'Admin',
              role: TextRole.ButtonLabel,
            })}
          </Button>
        </div>
      </div>
      <PasswordModal open={isPasswordModalOpen} onOpenChange={setIsPasswordModalOpen} />
    </div>
  );
};
